import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { injectIntl, FormattedMessage } from '../../../../components/Intl/withIntl.js';
import { Form, Button, ButtonGroup, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { getProductPrice, getProductSpecialPrices, getProductSpecialPrice, getProductSpecialPriceDateThreshold, getProductOption, memoizedGetProducts } from '../../ProductActions.js';
import { setCartItem, removeItem, setCartSubscriptionActive, getCartItemByProduct, getCartItemFromProduct, getCartQuantityStep } from '../../../Cart/CartActions.js';
import { getUserGroup, getUserGroupOption } from '../../../User/UserGroupActions.js';
import { getCurrentCurrency, defaultCurrency } from '../../../Currency/CurrencyActions.js';
import { getReferral } from '../../../App/AppActions.js';
import { getTranslation } from '../../../Intl/IntlHelper.js';

import { formatPrice } from '../../../../util/price.js';
import Icon from '../../../../components/Content/Icon.js';
import Price from '../../../Currency/components/view/Price.js';
import Chrono from '../../../../components/Time/Chrono.js';
import ProductWeight from './ProductWeight.js';
import ProductCapacityPrice from './ProductCapacityPrice.js';
import CmsPanel from '../../../Cms/components/content/CmsPanel.js';
import ProductSubscription from '../../../Cart/components/view/ProductSubscription.js';
import GamifiedButton from '../../../../components/Content/GamifiedButton.js';

// Conversion-oriented buy box (Claude Design template). Each real quantity
// special-price becomes a selectable "offer" card; the featured (best) tier is
// pre-selected. Selecting a card is purely visual — only the CTA commits to cart.
function ProductOfferSelector({ dispatch, intl, product, variation = null, item = null, products = [], userGroup = null, currency = defaultCurrency, referral = null, buttonColor = 'danger', onAddToCart = null, onChangeVariation = null }) {
	const [mounted, setMounted] = useState(false);
	const [selectedOffer, setSelectedOffer] = useState(null); // number | 'custom' | null(=default)
	const [customQty, setCustomQty] = useState(null);

	useEffect(() => {
		setMounted(true);
	}, []);

	const activeProduct = variation || product;
	const displayPriceHT = getUserGroupOption(userGroup, 'displayPriceHT');
	const key = displayPriceHT ? 'priceHT' : 'priceTTC';
	const step = getCartQuantityStep(activeProduct, userGroup);
	const baseQuantity = Math.max(activeProduct.minimumQuantity || 1, step);
	const catalogUnitPrice = activeProduct[key];
	const currentQuantity = mounted && item ? item.quantity : 0;

	const buildTier = quantity => {
		const total = getProductPrice(activeProduct, quantity, userGroup, !displayPriceHT, referral);
		const unit = total / quantity;
		return {
			quantity,
			total,
			unit,
			savings: (catalogUnitPrice * quantity) - total,
			percent: Math.round(100 - ((unit / catalogUnitPrice) * 100)),
			hasSaving: formatPrice(unit) < catalogUnitPrice,
		};
	};

	// Real quantity special-prices become the fixed offer cards.
	const tierQuantities = getProductSpecialPrices(activeProduct, key, null, userGroup, referral)
		.filter(specialPrice => specialPrice.quantity && parseInt(specialPrice.quantity, 10) > baseQuantity)
		.map(specialPrice => parseInt(specialPrice.quantity, 10))
		.filter((quantity, index, self) => self.indexOf(quantity) === index)
		.sort((a, b) => a - b);

	const baseTier = buildTier(baseQuantity);
	const tiers = tierQuantities.map(buildTier);
	const bestTier = tiers.length ? tiers[tiers.length - 1] : null;

	// Default visual selection = the featured tier (like the template's offerB = 2).
	const defaultOffer = bestTier ? bestTier.quantity : baseQuantity;
	const activeOffer = selectedOffer === null ? defaultOffer : selectedOffer;
	const activeCustomQty = customQty === null ? Math.max(step, (bestTier ? bestTier.quantity : baseQuantity) + step) : customQty;

	const datedSpecialPrice = getProductSpecialPrice(activeProduct, key, null, userGroup, referral);
	const countdownDate = datedSpecialPrice && getProductSpecialPriceDateThreshold(datedSpecialPrice);

	const commit = quantity => {
		dispatch(setCartItem(getCartItemFromProduct(
			{ ...activeProduct, variationParentId: variation && (product || {})._id },
			quantity,
			intl.locale,
			userGroup,
			{ referral },
		)));
	};

	const handleCustomStep = delta => {
		const next = Math.min(activeProduct.maximumQuantity || Infinity, Math.max(step, activeCustomQty + (delta * step)));
		setCustomQty(next);
		setSelectedOffer('custom');
	};

	const selectedQuantity = activeOffer === 'custom' ? activeCustomQty : activeOffer;
	// Gamified hint: the nearest quantity tier above what's already in the cart that still unlocks a discount.
	const nextPromoTier = tiers.find(tier => tier.percent > 0 && tier.quantity > currentQuantity);
	const remainingForPromo = nextPromoTier ? nextPromoTier.quantity - currentQuantity : 0;
	const showPromoHint = currentQuantity > 0 && !!nextPromoTier;

	// The selected offer is committed only on click. When the product is already in the cart and the
	// user picks a different quantity, flag the CTA (glow + hint) so they know they must apply it.
	// selectedOffer stays null until the user actually touches a card or the custom stepper.
	const isPendingChange = currentQuantity > 0 && selectedOffer !== null && selectedQuantity !== currentQuantity;

	const handleAddToCart = () => {
		commit(selectedQuantity);
		onAddToCart && onAddToCart(selectedQuantity);
	};

	const handleRemoveFromCart = () => {
		if(item) {
			dispatch(removeItem(item));
		}
	};

	const handleSubscribe = () => {
		if(!currentQuantity) {
			commit(baseQuantity);
		}
		dispatch(setCartSubscriptionActive());
	};

	const handleUnsubscribe = () => {
		dispatch(setCartSubscriptionActive(false));
	};

	const renderCartButtonLabel = () => {
		if(currentQuantity <= 0) {
			return <FormattedMessage id="cartAddTo" defaultMessage="Add to cart" />;
		}
		if(bestTier) {
			return <FormattedMessage id="cartMyEdit" defaultMessage="Edit my cart" />;
		}
		return <FormattedMessage id="cartModifyQuantity" defaultMessage="Change quantity" />;
	};

	const renderVariations = () => {
		if((product.variations || []).length && onChangeVariation) {
			const variations = product.variations.filter(variation => {
				const variationProduct = products.find(candidate => candidate._id === variation.productId);
				return variationProduct && (!(getProductOption(variationProduct, 'visibleGroups') || []).length || getProductOption(variationProduct, 'visibleGroups').includes((userGroup || {}).identifier));
			});
			if(variations.length) {
				return (
					<Form.Select className="mb-3" name="variation" value={(item || {}).productId || (variation || product)._id || ''} onChange={event => onChangeVariation(event.target.value)}>
						{
							variation
							? <option value="">{getTranslation(product, 'name', intl.locale)}</option>
							: <FormattedMessage id="selectOptions" defaultMessage="Select an option">{message => <option value="">{message}</option>}</FormattedMessage>
						}
						{variations.map(variation => <option key={variation.productId} value={variation.productId}>{getTranslation(variation, 'label', intl.locale)}</option>)}
					</Form.Select>
				);
			}
		}
	};

	// Radio: outer ring always dark (like the template), inner dot filled when selected.
	const renderRadio = selected => (
		<span className="d-inline-flex align-items-center justify-content-center rounded-circle border border-2 border-dark flex-shrink-0" style={{ width: '20px', height: '20px' }}>
			<span className={`d-inline-block rounded-circle ${selected ? 'bg-dark' : ''}`} style={{ width: '10px', height: '10px' }} />
		</span>
	);

	const cardClass = selected => `position-relative d-flex align-items-center gap-2 p-2 px-3 rounded-3 border ${selected ? 'border-2 border-dark bg-white shadow-sm' : 'bg-light'}`;

	const renderQuantityLabel = quantity => (
		activeProduct.weight > 0
		? <Fragment>{quantity} × <ProductWeight product={activeProduct} hideLabel /></Fragment>
		: <Fragment>{quantity} <FormattedMessage id="productPiece" defaultMessage="piece" /></Fragment>
	);

	const renderFixedCard = (tier, { featured = false } = {}) => {
		const selected = activeOffer === tier.quantity;
		return (
			<div role="button" tabIndex={0} onClick={() => setSelectedOffer(tier.quantity)} onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && setSelectedOffer(tier.quantity)} className={cardClass(selected)}>
				{renderRadio(selected)}
				<div className="flex-grow-1 lh-sm">
					<strong className="d-block text-dark">{renderQuantityLabel(tier.quantity)}</strong>
					{
						tier.savings > 0
						? <span className="small fw-bold text-success"><FormattedMessage id="productOfferSave" defaultMessage="Save {amount}" values={{ amount: <Price amount={tier.savings} formatPrice /> }} /></span>
						: <span className="small text-muted"><ProductCapacityPrice product={activeProduct} hideLabel /></span>
					}
					{featured && (
						<span className="d-block d-md-inline-block badge ms-2 rounded-pill bg-danger text-uppercase" style={{ top: '-0.1rem', fontSize: '0.62rem', letterSpacing: '0.04em' }}>
							⭐ <FormattedMessage id="productOfferBestValue" defaultMessage="Best value" /> {tier.percent > 0 ? `-${tier.percent}%` : ''}
						</span>
					)}
				</div>
				<div className="text-end lh-1">
					{tier.hasSaving && <span className="d-block small text-muted text-decoration-line-through"><Price amount={tier.total + tier.savings} formatPrice /></span>}
					<strong className="fs-5 text-danger"><Price amount={tier.total} formatPrice /></strong>
				</div>
			</div>
		);
	};

	const renderCustomCard = () => {
		const selected = activeOffer === 'custom';
		const total = getProductPrice(activeProduct, activeCustomQty, userGroup, !displayPriceHT, referral);
		const strikeTotal = catalogUnitPrice * activeCustomQty;
		return (
			<div role="button" tabIndex={0} onClick={() => setSelectedOffer('custom')} onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && setSelectedOffer('custom')} className={cardClass(selected)}>
				{renderRadio(selected)}
				<div className="flex-grow-1 lh-sm">
					<strong className="d-block text-dark"><FormattedMessage id="productOfferCustomQuantity" defaultMessage="Other quantity" /></strong>
					{bestTier && bestTier.percent > 0 && <span className="small text-muted"><FormattedMessage id="productOfferFromQuantity" defaultMessage="-{percent}% from {quantity} pieces" values={{ percent: bestTier.percent, quantity: bestTier.quantity }} /></span>}
					{formatPrice(total) < strikeTotal && <span className="small fw-bold text-success"><FormattedMessage id="productOfferSave" defaultMessage="Save {amount}" values={{ amount: <Price amount={strikeTotal - formatPrice(total)} formatPrice /> }} /></span>}
				</div>
				<div className="d-flex align-items-center bg-white rounded-3 border" onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()} role="presentation">
					<Button variant="link" size="sm" className="px-2 text-dark qty-control" onClick={() => handleCustomStep(-1)} disabled={activeCustomQty <= step}><Icon icon="remove" /></Button>
					<span className="px-1 fw-bold text-dark" style={{ minWidth: '1.5rem', textAlign: 'center' }}>{activeCustomQty}</span>
					<Button variant="link" size="sm" className="px-2 text-dark qty-control" onClick={() => handleCustomStep(1)} disabled={activeProduct.maximumQuantity && activeCustomQty >= activeProduct.maximumQuantity}><Icon icon="add" /></Button>
				</div>
				<div className="text-end lh-1">
					{formatPrice(total) < strikeTotal && <span className="d-block small text-muted text-decoration-line-through"><Price amount={strikeTotal} formatPrice /></span>}
					<strong className="fs-5 text-danger"><Price amount={total} formatPrice /></strong>
				</div>
			</div>
		);
	};

	return (
		<Fragment>
			{renderVariations()}

			<div className="d-flex flex-column gap-2">
				{renderFixedCard(baseTier)}
				{tiers.map((tier, index) => (
					<Fragment key={tier.quantity}>{renderFixedCard(tier, { featured: index === tiers.length - 1 })}</Fragment>
				))}
				{renderCustomCard()}

				{mounted && countdownDate && (
					<div className="d-flex flex-wrap align-items-center justify-content-center gap-2 px-3 py-2 rounded-3 bg-primary text-white fw-bold small">
						<span className="d-none d-md-inline spinner-grow spinner-grow-sm text-warning" role="status" aria-hidden="true" />
						<span className="">🔥 <FormattedMessage id="productSpecialPriceDateThresholdLimit" defaultMessage="Offer ends in {chrono}" values={{ chrono: <span className="bg-opacity-25 text-warning small"><Chrono date={countdownDate} displayText /></span> }} /></span>
					</div>
				)}

				<div className="small text-muted">
					<CmsPanel slug="shipping-information-small" mode="compact" forceColumn />
				</div>

				{showPromoHint && (
					<div className="small fw-bold text-success text-center">
						🎁 <FormattedMessage id="productOfferAddMore" defaultMessage="Add {quantity} more to get -{percent}%" values={{ quantity: remainingForPromo, percent: nextPromoTier.percent }} />
					</div>
				)}

				<div className="d-md-flex justify-content-between text-center">
					<OverlayTrigger
						placement="top"
						show={isPendingChange}
						overlay={
							<Tooltip id="cart-pending-tooltip">
								👇 <FormattedMessage id="cartPendingUpdate" defaultMessage="Click to apply this quantity to your cart" />
							</Tooltip>
						}
					>
						<span className="d-inline-block w-100 w-md-auto">
							<GamifiedButton as={Button} variant={buttonColor} size="lg" className={`w-100 fw-bold rounded-3 add-to-cart${isPendingChange ? ' btn-cart-dirty' : ''}`} onClick={handleAddToCart}>
								{renderCartButtonLabel()} <Icon icon="shopping-cart" />
							</GamifiedButton>
						</span>
					</OverlayTrigger>
					{currentQuantity > 0 && (
						<Fragment>
							<span className="text-black-50 mx-3 mx-md-0 my-auto">
								<FormattedMessage id="cartItemQuantity" defaultMessage="{count} item(s) in my cart" values={{ count: currentQuantity }} />
							</span>
							<Button variant="link" size="" className="m-0 p-0 text-success see-cart" as={Link} to={`/${intl.locale}/cart`}>
								<FormattedMessage id="cartSee" defaultMessage="See my cart" /> <Icon icon="chevron-right" />
							</Button>
						</Fragment>
					)}
				</div>

				{currentQuantity > 0 && (
					<div className="text-center">
						<Button variant="link" size="sm" className="p-0 text-muted small text-decoration-none remove-from-cart" onClick={handleRemoveFromCart}>
							<Icon icon="trash" /> <FormattedMessage id="cartRemoveProduct" defaultMessage="Remove from cart" />
						</Button>
					</div>
				)}

				<ProductSubscription product={product} variation={variation} onSubscribe={handleSubscribe} onUnSubscribe={handleUnsubscribe} />

			</div>
		</Fragment>
	);
}

function mapStateToProps(store, props) {
	return {
		currency: getCurrentCurrency(store),
		userGroup: getUserGroup(store),
		item: getCartItemByProduct(store, props.variation || props.product),
		products: memoizedGetProducts(store, { isActive: true, isVariation: true }),
		referral: getReferral(store),
	};
}

ProductOfferSelector.propTypes = {
	dispatch: PropTypes.func.isRequired,
	intl: PropTypes.object.isRequired,
	product: PropTypes.object.isRequired,
	variation: PropTypes.object,
	item: PropTypes.object,
	products: PropTypes.arrayOf(PropTypes.object),
	userGroup: PropTypes.object,
	currency: PropTypes.string,
	referral: PropTypes.string,
	buttonColor: PropTypes.string,
	onAddToCart: PropTypes.func,
	onChangeVariation: PropTypes.func,
};

export default connect(mapStateToProps)(injectIntl(ProductOfferSelector));
